"use client"

import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Badge } from "@/components/ui/badge"
import { Shield, Zap, BarChart3, Users, Lock, CheckCircle, ArrowRight, Activity } from "lucide-react"
import { useLanguage } from '@/lib/language-context'
import { getTranslation } from '@/lib/translations'
import { LanguageSwitcher } from '@/components/language-switcher'
import { track } from '@/lib/analytics'

// Structured data for the home page
const homeStructuredData = {
  "@context": "https://schema.org",
  "@type": ["SoftwareApplication", "WebApplication"],
  "name": "AuthFlow™",
  "description": "Simple, secure email/password authentication with JWT tokens for modern web applications. Unlike complex platforms, we focus on what matters: reliable login that just works.",
  "url": "https://www.authflow.net",
  "applicationCategory": ["SecurityApplication", "DeveloperApplication"],
  "operatingSystem": "Web Browser",
  "offers": {
    "@type": "Offer",
    "price": "0",
    "priceCurrency": "USD",
    "description": "Free tier available, paid plans start at $9.99/month"
  },
  "featureList": [
    "Email/Password Authentication",
    "JWT Token Management",
    "Secure Session Handling",
    "Developer-Friendly API",
    "Simple Integration",
    "Reliable Security"
  ],
  "screenshot": "https://www.authflow.net/authflow-logo.svg",
  "author": {
    "@type": "Organization",
    "name": "AuthFlow™"
  },
  "publisher": {
    "@type": "Organization",
    "name": "AuthFlow™"
  },
  "datePublished": "2023-01-01",
  "softwareVersion": "1.0",
  "applicationSubCategory": "Authentication Platform"
}

export default function HomePageContent() {
  const { language } = useLanguage()
  const heroMetrics = [
    {
      value: getTranslation(language, 'hero.metrics.firstIntegrationValue'),
      label: getTranslation(language, 'hero.metrics.firstIntegrationLabel'),
    },
    {
      value: getTranslation(language, 'hero.metrics.predictableSessionsValue'),
      label: getTranslation(language, 'hero.metrics.predictableSessionsLabel'),
    },
    {
      value: getTranslation(language, 'hero.metrics.noMazeValue'),
      label: getTranslation(language, 'hero.metrics.noMazeLabel'),
    },
  ]

  return (
    <div className="min-h-screen bg-[#0d1b2f] text-white">
      {/* Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify(homeStructuredData),
        }}
      />

      {/* Header */}
      <header className="sticky top-0 z-40 border-b border-white/12 bg-[#0d1b2f]/72 backdrop-blur-xl supports-[backdrop-filter]:bg-[#0d1b2f]/62">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="flex h-16 items-center justify-between">
            <a href="/" className="flex items-center space-x-2">
              <div className="flex h-9 w-9 items-center justify-center rounded-2xl border border-[#ffa500]/55 bg-[#ffa500]/20 shadow-[0_0_44px_rgba(255,165,0,0.34)]">
                <Shield className="h-5 w-5 text-[#fff7e6]" aria-hidden="true" />
              </div>
              <span className="text-xl font-semibold tracking-tight text-white">AuthFlow™</span>
            </a>
            <nav id="navigation" className="hidden xl:flex items-center gap-6" role="navigation" aria-label="Main navigation">
              <a href="/#features" className="text-[19px] font-semibold tracking-[0.01em] text-white/82 subpixel-antialiased transition-colors hover:text-white">
                {getTranslation(language, 'nav.features')}
              </a>
              <a href="/pricing" className="text-[19px] font-semibold tracking-[0.01em] text-white/82 subpixel-antialiased transition-colors hover:text-white">
                {getTranslation(language, 'nav.pricing')}
              </a>
              <a href="/comparisons" className="text-[19px] font-semibold tracking-[0.01em] text-white/82 subpixel-antialiased transition-colors hover:text-white">
                {getTranslation(language, 'nav.compare')}
              </a>
              <a href="mailto:support@authflow.net" className="text-[19px] font-semibold tracking-[0.01em] text-white/82 subpixel-antialiased transition-colors hover:text-white">
                {getTranslation(language, 'nav.contact')}
              </a>
            </nav>
            <div className="flex items-center gap-2 sm:gap-4">
              <LanguageSwitcher className="h-10 px-3.5 text-[15px] font-medium tracking-[0.01em] text-white/82 subpixel-antialiased hover:bg-white/5 hover:text-white focus:bg-white/10 focus:text-white" />
              <a href="/sign-in">
                <Button variant="ghost" className="h-10 px-3.5 text-[15px] font-medium tracking-[0.01em] text-white/82 subpixel-antialiased hover:bg-white/5 hover:text-white">
                  {getTranslation(language, 'nav.signIn')}
                </Button>
              </a>
              <a href="/get-started">
                <Button className="border border-[#ffa500]/60 bg-[#ffa500] text-slate-950 shadow-[0_20px_64px_rgba(255,165,0,0.34)] hover:bg-[#ffb733]">{getTranslation(language, 'nav.getStarted')}</Button>
              </a>
            </div>
          </div>
        </div>
      </header>

      <main id="main-content" role="main">
      {/* Hero Section */}
      <section className="relative overflow-hidden pb-20 pt-16 lg:pb-28 lg:pt-24" aria-labelledby="hero-heading">
        <div className="authflow-grid absolute inset-0 opacity-48" />
        <div className="authflow-noise absolute inset-0 opacity-18" />
        <div className="authflow-orb authflow-orb-left" />
        <div className="authflow-orb authflow-orb-right" />
        <div className="authflow-beam authflow-beam-one" />
        <div className="authflow-beam authflow-beam-two" />

        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid items-center gap-14 lg:grid-cols-[minmax(0,1.1fr)_minmax(340px,0.9fr)] lg:gap-10">
            <div className="max-w-3xl">
              <Badge variant="secondary" className="mb-6 border border-cyan-300/20 bg-cyan-300/10 px-4 py-1.5 text-cyan-100">
                {getTranslation(language, 'hero.badge')}
              </Badge>
              <h1 id="hero-heading" className="max-w-4xl text-5xl font-semibold tracking-[-0.04em] text-white sm:text-6xl lg:text-7xl lg:leading-[0.94]">
                {getTranslation(language, 'hero.title')}
                <span className="mt-2 block bg-gradient-to-r from-[#ffa500] via-white to-cyan-200 bg-clip-text text-transparent">
                  {getTranslation(language, 'hero.subtitle')}
                </span>
              </h1>
              <p className="mt-7 max-w-2xl text-lg leading-8 text-slate-300 sm:text-xl">
                {getTranslation(language, 'hero.description')}
              </p>
              <div className="mt-9 flex flex-col gap-4 sm:flex-row">
                <a href="/sign-up" onClick={() => track('cta_click', { button: 'hero_start_trial', location: 'home_hero' })}>
                  <Button size="lg" className="h-13 rounded-full border border-[#ffa500]/60 bg-[#ffa500] px-8 text-base font-semibold text-slate-950 shadow-[0_24px_76px_rgba(255,165,0,0.38)] hover:bg-[#ffb733]">
                    {getTranslation(language, 'hero.startTrial')}
                  </Button>
                </a>
                <a href="/get-started" onClick={() => track('cta_click', { button: 'hero_get_started', location: 'home_hero' })}>
                  <Button size="lg" variant="outline" className="h-13 rounded-full border-white/15 bg-white/5 px-8 text-base text-white backdrop-blur-md hover:bg-white/10 hover:text-white">
                    {getTranslation(language, 'hero.getStarted')}
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </a>
              </div>
              <p className="mt-4 text-sm text-slate-400">
                {getTranslation(language, 'hero.trialNote')}
              </p>

              <div className="mt-10 grid gap-4 sm:grid-cols-3">
                {heroMetrics.map((metric) => (
                  <div key={metric.label} className="rounded-3xl border border-white/10 bg-white/5 px-5 py-5 backdrop-blur-md shadow-[0_12px_40px_rgba(0,0,0,0.18)]">
                    <div className="text-xl font-semibold text-white">{metric.value}</div>
                    <div className="mt-1 text-sm leading-6 text-slate-400">{metric.label}</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="relative lg:pl-6">
              <div className="absolute -left-4 top-8 hidden h-32 w-32 rounded-full bg-cyan-300/15 blur-3xl lg:block" />
              <div className="absolute -right-8 bottom-10 hidden h-36 w-36 rounded-full bg-amber-200/10 blur-3xl lg:block" />

              <div className="relative overflow-hidden rounded-[2rem] border border-white/12 bg-[linear-gradient(180deg,rgba(29,43,66,0.92),rgba(16,28,45,0.88))] p-5 shadow-[0_26px_100px_rgba(5,14,30,0.5)]">
                <div className="mb-5 flex items-center justify-between rounded-2xl border border-white/8 bg-white/5 px-4 py-3">
                  <div>
                    <div className="text-xs uppercase tracking-[0.24em] text-slate-400">{getTranslation(language, 'hero.missionControlLabel')}</div>
                    <div className="mt-1 text-lg font-medium text-white">{getTranslation(language, 'hero.missionControlTitle')}</div>
                  </div>
                  <div className="flex items-center gap-2 rounded-full border border-emerald-400/20 bg-emerald-400/10 px-3 py-1 text-xs text-emerald-200">
                    <Activity className="h-3.5 w-3.5" />
                    {getTranslation(language, 'hero.missionControlStatus')}
                  </div>
                </div>

                <div className="grid gap-4">
                  <div className="rounded-[1.5rem] border border-white/10 bg-[#13243a] p-4">
                    <div className="flex items-center justify-between text-xs uppercase tracking-[0.24em] text-slate-400">
                      <span>{getTranslation(language, 'hero.sessionLayer')}</span>
                      <span>JWT</span>
                    </div>
                    <div className="mt-4 rounded-2xl border border-cyan-300/10 bg-cyan-300/5 p-4">
                      <div className="font-mono text-sm text-cyan-100">sessionToken.verify()</div>
                      <div className="mt-3 flex items-center justify-between rounded-xl border border-white/6 bg-white/5 px-3 py-2">
                        <span className="text-sm text-slate-300">{getTranslation(language, 'hero.emailPasswordSignIn')}</span>
                        <span className="rounded-full bg-emerald-400/15 px-2.5 py-1 text-xs text-emerald-200">{getTranslation(language, 'hero.validated')}</span>
                      </div>
                      <div className="mt-2 flex items-center justify-between rounded-xl border border-white/6 bg-white/5 px-3 py-2">
                        <span className="text-sm text-slate-300">{getTranslation(language, 'hero.roleAwareVerification')}</span>
                        <span className="rounded-full bg-amber-300/15 px-2.5 py-1 text-xs text-amber-100">{getTranslation(language, 'hero.live')}</span>
                      </div>
                    </div>
                  </div>

                  <div className="grid gap-4 sm:grid-cols-2">
                    <div className="rounded-[1.5rem] border border-white/8 bg-white/5 p-4">
                      <div className="flex items-center gap-3 text-white">
                        <div className="rounded-2xl bg-amber-300/12 p-3 text-amber-200">
                          <BarChart3 className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">{getTranslation(language, 'hero.integrationShapeLabel')}</div>
                          <div className="text-lg font-semibold">{getTranslation(language, 'hero.integrationShapeValue')}</div>
                        </div>
                      </div>
                    </div>
                    <div className="rounded-[1.5rem] border border-white/8 bg-white/5 p-4">
                      <div className="flex items-center gap-3 text-white">
                        <div className="rounded-2xl bg-cyan-300/12 p-3 text-cyan-200">
                          <Users className="h-5 w-5" />
                        </div>
                        <div>
                          <div className="text-sm text-slate-400">{getTranslation(language, 'hero.forTeamsLabel')}</div>
                          <div className="text-lg font-semibold">{getTranslation(language, 'hero.forTeamsValue')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Feature Highlights */}
      <section id="features" className="border-t border-white/10 bg-[linear-gradient(180deg,#12243a_0%,#162b43_100%)] py-24" aria-labelledby="features-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="features-heading" className="text-3xl font-semibold tracking-[-0.03em] text-white lg:text-5xl mb-4">
              {getTranslation(language, 'features.title')}
            </h2>
            <p className="mx-auto max-w-2xl text-lg leading-8 text-slate-400 sm:text-xl">
              {getTranslation(language, 'features.description')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-white/10 bg-white/[0.04] text-white shadow-[0_20px_60px_rgba(0,0,0,0.22)] backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 hover:border-cyan-300/20">
              <CardHeader>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-cyan-300/10 text-cyan-200">
                  <Zap className="h-6 w-6" aria-hidden="true" />
                </div>
                <CardTitle className="text-white">{getTranslation(language, 'features.emailPassword.title')}</CardTitle>
                <CardDescription className="text-slate-400">
                  {getTranslation(language, 'features.emailPassword.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-white/10 bg-white/[0.04] text-white shadow-[0_20px_60px_rgba(0,0,0,0.22)] backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 hover:border-amber-300/20">
              <CardHeader>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-amber-300/10 text-amber-200">
                  <Lock className="h-6 w-6" aria-hidden="true" />
                </div>
                <CardTitle className="text-white">{getTranslation(language, 'features.jwtTokens.title')}</CardTitle>
                <CardDescription className="text-slate-400">
                  {getTranslation(language, 'features.jwtTokens.description')}
                </CardDescription>
              </CardHeader>
            </Card>

            <Card className="border-white/10 bg-white/[0.04] text-white shadow-[0_20px_60px_rgba(0,0,0,0.22)] backdrop-blur-md transition-transform duration-300 hover:-translate-y-1 hover:border-emerald-300/20">
              <CardHeader>
                <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-300/10 text-emerald-200">
                  <CheckCircle className="h-6 w-6" aria-hidden="true" />
                </div>
                <CardTitle className="text-white">{getTranslation(language, 'features.developerApi.title')}</CardTitle>
                <CardDescription className="text-slate-400">
                  {getTranslation(language, 'features.developerApi.description')}
                </CardDescription>
              </CardHeader>
            </Card>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      {/* <section className="py-20" aria-labelledby="testimonials-heading">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 id="testimonials-heading" className="text-3xl lg:text-4xl font-bold text-foreground mb-4">{getTranslation(language, 'testimonials.title')}</h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8" role="list">
            <Card className="bg-muted border-border" role="listitem">
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">
                  {getTranslation(language, 'testimonials.testimonial1.text')}
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center mr-3">
                    <span className="text-accent font-semibold">SJ</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{getTranslation(language, 'testimonials.testimonial1.author')}</p>
                    <p className="text-sm text-muted-foreground">{getTranslation(language, 'testimonials.testimonial1.role')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted border-border" role="listitem">
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">
                  {getTranslation(language, 'testimonials.testimonial2.text')}
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center mr-3">
                    <span className="text-accent font-semibold">MR</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{getTranslation(language, 'testimonials.testimonial2.author')}</p>
                    <p className="text-sm text-muted-foreground">{getTranslation(language, 'testimonials.testimonial2.role')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-muted border-border" role="listitem">
              <CardContent className="pt-6">
                <p className="text-muted-foreground mb-4">
                  {getTranslation(language, 'testimonials.testimonial3.text')}
                </p>
                <div className="flex items-center">
                  <div className="w-10 h-10 bg-accent/20 rounded-full flex items-center justify-center mr-3">
                    <span className="text-accent font-semibold">AL</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{getTranslation(language, 'testimonials.testimonial3.author')}</p>
                    <p className="text-sm text-muted-foreground">{getTranslation(language, 'testimonials.testimonial3.role')}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section> */}

      {/* CTA Section */}
      <section className="border-t border-white/10 bg-[radial-gradient(circle_at_top,rgba(250,204,21,0.22),transparent_34%),linear-gradient(180deg,#152941_0%,#132338_100%)] py-24" aria-labelledby="cta-heading">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-3xl rounded-[2rem] border border-white/10 bg-white/[0.04] px-6 py-12 text-center shadow-[0_25px_90px_rgba(0,0,0,0.28)] backdrop-blur-md sm:px-10">
            <h2 id="cta-heading" className="mb-6 text-3xl font-semibold tracking-[-0.03em] text-white lg:text-5xl">
              {getTranslation(language, 'cta.title')}
            </h2>
            <p className="mb-8 text-lg leading-8 text-slate-300 sm:text-xl">
              {getTranslation(language, 'cta.description')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a href="/sign-up">
                <Button size="lg" className="h-13 rounded-full border border-[#ffa500]/60 bg-[#ffa500] px-8 text-base font-semibold text-slate-950 shadow-[0_20px_64px_rgba(255,165,0,0.34)] hover:bg-[#ffb733]">
                  {getTranslation(language, 'cta.startTrial')}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>
      </main>

      {/* Footer */}
      <footer className="border-t border-white/10 bg-[#102236] py-12" role="contentinfo">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-4 gap-8">
            <div className="md:col-span-2">
              <div className="flex items-center space-x-2 mb-4">
                <Shield className="h-6 w-6 text-amber-300" aria-hidden="true" />
                <span className="text-lg font-bold text-white">AuthFlow™</span>
              </div>
              <p className="mb-4 max-w-md text-slate-300">
                {getTranslation(language, 'footer.description')}
              </p>
              <p className="text-sm text-slate-200">
                {getTranslation(language, 'footer.copyright')}
              </p>
            </div>

            <div>
              <h3 className="mb-4 font-semibold text-white">{getTranslation(language, 'footer.product')}</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/#features" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'nav.features')}</a></li>
                <li><a href="/pricing" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'nav.pricing')}</a></li>
                <li><a href="/comparisons" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'nav.compare')}</a></li>
                <li><a href="/get-started" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'footer.apiReference')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="mb-4 font-semibold text-white">{getTranslation(language, 'footer.resources')}</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/get-started" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'footer.about')}</a></li>
                <li><a href="/get-started" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'footer.apiReference')}</a></li>
                <li><a href="mailto:support@authflow.net" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'footer.support')}</a></li>
              </ul>
            </div>

            <div>
              <h3 className="mb-4 font-semibold text-white">{getTranslation(language, 'footer.company')}</h3>
              <ul className="space-y-2 text-sm">
                <li><a href="/about" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'footer.about')}</a></li>
                <li><a href="/privacy-policy" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'footer.privacyPolicy')}</a></li>
                <li><a href="/terms-of-service" className="text-slate-300 hover:text-white transition-colors">{getTranslation(language, 'footer.termsOfService')}</a></li>
              </ul>
            </div>
          </div>
        </div>
      </footer>
    </div>
  )
}